import { X, MapPin, Clock, ExternalLink, Calendar, Tag, Navigation, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useEffect, useState, useRef } from 'react';
import { toast } from 'sonner';

export default function EventInfoPanel({ event, onClose, userLocation, markerPosition }) {
  const [distance, setDistance] = useState(null);
  const [panelStyle, setPanelStyle] = useState({});
  const panelRef = useRef(null);

  useEffect(() => {
    if (userLocation && event) {
      const dist = calculateDistance(
        userLocation.lat,
        userLocation.lng,
        event.position.lat,
        event.position.lng
      );
      setDistance(dist);
    }
  }, [userLocation, event]);

  useEffect(() => {
    // Position panel to the right of center on desktop (after centering)
    if (event && window.innerWidth >= 768) {
      // Wait a bit for map centering animation to complete
      setTimeout(() => {
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const panelWidth = 384; // w-96 = 24rem = 384px
        const panelHeight = Math.min(480, windowHeight - 180); // Dynamic height with more padding
        
        // Position to the right of center
        const centerX = windowWidth / 2;
        
        let left = centerX + 80; // Position to the right of center
        
        // Calculate top position to ensure panel is fully visible with good bottom spacing
        // Start from a comfortable position from top, ensuring bottom has at least 100px space
        const minTopPadding = 100; // Space from top
        const minBottomPadding = 100; // Space from bottom
        let top = minTopPadding;
        
        // If panel would go below viewport with bottom padding, move it up
        if (top + panelHeight > windowHeight - minBottomPadding) {
          top = windowHeight - panelHeight - minBottomPadding;
        }
        
        // Ensure it doesn't go above minimum top padding
        if (top < minTopPadding) top = minTopPadding;
        
        // Ensure panel stays within horizontal viewport with padding
        const horizontalPadding = 20;
        if (left + panelWidth > windowWidth - horizontalPadding) {
          left = centerX - panelWidth - 80; // Position to left if overflow right
          
          // If still overflowing on left, center it
          if (left < horizontalPadding) {
            left = (windowWidth - panelWidth) / 2;
          }
        }
        
        // Final safety check - ensure left is always positive
        if (left < horizontalPadding) left = horizontalPadding;
        
        setPanelStyle({
          left: `${left}px`,
          top: `${top}px`,
          right: 'auto'
        });
      }, 300); // Wait for centering animation
    } else {
      setPanelStyle({});
    }
  }, [event]);

  // Handle click outside to close panel (works on mobile, iPad, and desktop)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        // Check if click is not on a map marker (they have class 'custom-marker' or 'cluster-marker')
        const clickedElement = event.target;
        const isMarkerClick = clickedElement.closest('.custom-marker') || 
                             clickedElement.closest('.cluster-marker') ||
                             clickedElement.closest('[class*="marker"]');
        
        if (!isMarkerClick) {
          onClose();
        }
      }
    };

    // Add event listener with a slight delay to avoid closing immediately on marker click
    // Use both mousedown (desktop) and touchstart (mobile/iPad)
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [onClose]);

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius of the Earth in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    return distance.toFixed(1);
  };

  const handleNavigate = () => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${event.position.lat},${event.position.lng}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleRegister = () => {
    window.open(event.registrationUrl, '_blank', 'noopener,noreferrer');
  };

  const handleShare = async () => {
    // Create shareable URL with event ID - using _id field
    const eventId = event._id || event.id;
    const shareUrl = `${window.location.origin}${window.location.pathname}?event=${eventId}`;
    
    // Try native share API first (works on mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: event.title,
          text: `Check out this event: ${event.title}`,
          url: shareUrl,
        });
        // Don't show success toast - native share dialog is enough feedback
      } catch (error) {
        // User cancelled or error occurred
        if (error.name !== 'AbortError') {
          console.error('Share error:', error);
          // Fallback to copy
          copyToClipboard(shareUrl);
        }
      }
    } else {
      // Fallback to copying link
      copyToClipboard(shareUrl);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Link copied to clipboard!');
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success('Link copied to clipboard!');
      } catch (err) {
        toast.error('Failed to copy link');
      }
      document.body.removeChild(textArea);
    }
  };

  if (!event) return null;

  const getTypeLabel = (type) => {
    return type === 'invite-only' ? 'Invite Only' : 'Open';
  };

  return (
    <div 
      ref={panelRef}
      className="fixed md:absolute left-2 right-2 md:left-auto md:w-96 z-40 animate-fade-in
                 top-12 sm:top-16 bottom-16 md:top-auto md:bottom-auto
                 flex flex-col md:block
                 max-w-[calc(100vw-1rem)] md:max-w-none" 
      style={panelStyle}
    >
      <Card className="glass-effect border border-border shadow-xl rounded-t-2xl md:rounded-2xl 
                       flex-1 md:flex-none
                       flex flex-col
                       md:max-h-[480px] 
                       overflow-hidden h-full md:h-auto">
        {/* Fixed Header with share and close buttons */}
        <div className="relative flex-shrink-0">
          <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-primary/20 to-transparent" />
          <div className="absolute top-3 right-3 flex items-center gap-2 z-10">
            <button
              onClick={handleShare}
              className="p-2 bg-card/80 hover:bg-card rounded-full transition-smooth shadow-md"
              aria-label="Share event"
            >
              <Share2 className="h-4 w-4 text-foreground" />
            </button>
            <button
              onClick={onClose}
              className="p-2 bg-card/80 hover:bg-card rounded-full transition-smooth shadow-md"
              aria-label="Close"
            >
              <X className="h-4 w-4 text-foreground" />
            </button>
          </div>
          
          <div className="pt-6 px-6 pb-4 relative">
            <div className="flex items-start space-x-2 sm:space-x-3">
              <div className="text-2xl sm:text-4xl flex-shrink-0">{event.icon}</div>
              <div className="flex-1 min-w-0">
                <h2 className="text-base sm:text-xl font-bold text-foreground mb-2 leading-tight">{event.title}</h2>
                <div className="flex flex-wrap gap-1">
                  {event.l1_category && (
                    <Badge variant="secondary" className="capitalize text-xs">
                      {event.l1_category}
                    </Badge>
                  )}
                  {event.l2_category && (
                    <Badge variant="outline" className="text-xs">
                      <span className="mr-1">{event.icon}</span>
                      {event.l2_category.replace(/_/g, ' ')}
                    </Badge>
                  )}
                  {!event.l1_category && event.category && (
                    <Badge variant="secondary" className="capitalize">
                      <Tag className="h-3 w-3 mr-1" />
                      {event.category}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <Separator className="flex-shrink-0" />

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto scrollbar-styled">
          {/* Event Details */}
          <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-foreground leading-relaxed">{event.description}</p>
          
          {/* Info Grid - Date spans full width, Time and Location below */}
          <div className="space-y-3">
            {/* Date - Full Width */}
            <div className="flex items-start space-x-2 p-3 bg-muted/50 rounded-lg">
              <Calendar className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground mb-1">Date</p>
                <p className="text-base font-semibold text-foreground">
                  {event.isRecurring && event.recurringDays && event.recurringDays.length > 0 ? (
                    `Every ${event.recurringDays.join(', ')}`
                  ) : (
                    new Date(event.eventDate).toLocaleDateString('en-US', { 
                      weekday: 'short', 
                      month: 'short', 
                      day: 'numeric',
                      year: 'numeric'
                    })
                  )}
                </p>
              </div>
            </div>
            
            {/* Time and Location - Stacked on Mobile, Side by Side on Desktop */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-start space-x-2 p-3 bg-muted/50 rounded-lg">
                <Clock className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-muted-foreground">Time</p>
                  <p className="text-sm font-medium text-foreground">{event.startTime} - {event.endTime}</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-2 p-3 bg-muted/50 rounded-lg">
                <MapPin className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-muted-foreground">Location</p>
                  <p className="text-sm font-medium text-foreground">
                    {event.location}{event.city && `, ${event.city}`}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Distance if location available */}
          {distance && (
            <div className="flex items-center space-x-2 p-3 bg-accent-light border border-accent/20 rounded-lg">
              <Navigation className="h-4 w-4 text-accent" />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">{distance} km from your location</p>
              </div>
            </div>
          )}

          {/* Price Badge */}
          {event.price && (
            <div className="flex items-center justify-between p-3 bg-muted/50 border border-border rounded-lg">
              <span className="text-sm font-medium text-foreground">Entry Fee</span>
              <span className="text-lg font-bold text-primary">{event.price}</span>
            </div>
          )}
          </div>
          {/* End Event Details */}

          <Separator />

          {/* Action Buttons */}
          <div className="px-6 py-4 space-y-3">
            {/* Registration Section - Moved above navigation */}
            {event.requiresRegistration ? (
              <>
                <Button
                  onClick={handleRegister}
                  className="w-full gradient-primary text-primary-foreground hover:opacity-90 shadow-primary transition-smooth font-semibold"
                  size="lg"
                >
                  Register Now
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  You'll be redirected to the event registration page
                </p>
              </>
            ) : (
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <p className="text-sm font-medium text-foreground">No registration required</p>
                <p className="text-xs text-muted-foreground mt-1">Just show up and join the fun!</p>
              </div>
            )}

            {/* Navigate Button - Moved below registration */}
            {distance && (
              <Button
                onClick={handleNavigate}
                variant="outline"
                className="w-full border-primary text-primary hover:bg-primary/10 transition-smooth font-medium"
                size="lg"
              >
                <Navigation className="mr-2 h-4 w-4" />
                Navigate in Google Maps
              </Button>
            )}
          </div>
        </div>
        {/* End of scrollable content */}
      </Card>
    </div>
  );
}
